import React, { useMemo, memo } from 'react';
import { useQuery } from '@apollo/client';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { ColGroup, TableBody, TableHead } from '../../../../search-results-table';
import { CustomDynamicResultTable } from '../../../../custom-dynamic-result-table';
import {
  fetchCloudSitesForTransactionAccount,
  fetchCloudSitesForBillingpProfile,
} from '../queries/fetch-entitlement-cloud-site-ids';
import { CustomPanel } from '../../../../custom-panel';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import './cloud-site-linked-subscriptions-tab.scss';
import { ProductAppSubscriptionsContainer } from '../../../../product-app-subscriptions-container';
import { useFetchNonCloudSiteLinkedEntitlements } from '../use-fetch-non-cloudsite-linked-entitlements';
import DownloadSubscriptionToCsv from './download-subscription-to-csv-button';

export interface CloudSiteLinkedSubscriptionTabProps {
  transactionAccountId?: string;
  billingProfileId?: string;
  label: string;
  shouldShowDownloadCSV?: boolean;
}

export const emptyStateProps = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
  testId: 'no-results-cloudsite-linked-subscriptions',
};
const columnsForTxa = [
  {
    name: 'Subscription',
    customClassNames: 'col-30',
  },
  {
    name: 'Usage',
    customClassNames: 'col-10',
  },
  {
    name: 'Next bill/Expiry',
    customClassNames: 'col-10',
  },
  {
    name: 'Billing cycle',
    customClassNames: 'col-10',
  },
  {
    name: 'Payment method',
    customClassNames: 'col-15',
  },
  {
    name: 'Sold-to address',
    customClassNames: 'col-20',
  },
  {
    name: '',
    customClassNames: 'col-5',
  },
];

const columnsForBillingProfile = [
  {
    name: 'Subscription',
    customClassNames: 'col-40',
  },
  {
    name: 'Usage',
    customClassNames: 'col-15',
  },
  {
    name: 'Billing cycle',
    customClassNames: 'col-15',
  },
  {
    name: 'Next bill/Expiry',
    customClassNames: 'col-20',
  },
  {
    name: '',
    customClassNames: 'col-10',
  },
];

type GetCloudSiteIdsProps = generatedTypesNew.Maybe<
  {
    __typename?: 'CloudSiteDetail' | undefined;
  } & Pick<generatedTypesNew.CloudSiteDetail, 'cloudSiteId' | 'cloudSiteHostname'>
>[];

export const getCloudSiteIds = (cloudSites: GetCloudSiteIdsProps) => {
  const cloudSiteIds: string[] = [];
  if (cloudSites?.length > 0) {
    cloudSites?.forEach((cloudSite) => cloudSite?.cloudSiteId && cloudSiteIds.push(cloudSite?.cloudSiteId));
  }
  return cloudSiteIds;
};

export const CloudSiteLinkedSubscriptionTab = ({
  transactionAccountId,
  billingProfileId,
  label,
  shouldShowDownloadCSV,
}: CloudSiteLinkedSubscriptionTabProps) => {
  const { data, loading, error } = useQuery<
    generatedTypesNew.FetchCloudSitesForTransactionAccountQuery,
    generatedTypesNew.FetchCloudSitesForTransactionAccountQueryVariables
  >(fetchCloudSitesForTransactionAccount, {
    variables: {
      id: transactionAccountId as string,
      searchOptions: {
        first: 100,
      },
    },
    skip: !transactionAccountId,
    nextFetchPolicy: 'cache-and-network',
  });

  const {
    data: cloudSitesForBillingProfileData,
    loading: cloudSitesForBillingProfileLoading,
    error: cloudSitesForBillingProfileError,
  } = useQuery<
    generatedTypesNew.FetchCloudSitesForBillingpProfileQuery,
    generatedTypesNew.FetchCloudSitesForBillingpProfileQueryVariables
  >(fetchCloudSitesForBillingpProfile, {
    variables: {
      id: billingProfileId as string,
    },
    skip: !billingProfileId,
    nextFetchPolicy: 'cache-and-network',
  });

  const {
    error: unprovisionedDataError,
    loading: unprovisionedDataLoading,
    isEmptyUnprovisionedData,
  } = useFetchNonCloudSiteLinkedEntitlements({
    transactionAccountId,
    billingProfileId,
  });

  const cloudSites = useMemo(
    () =>
      (transactionAccountId
        ? data?.cloudSitesForTransactionAccount
        : cloudSitesForBillingProfileData?.cloudSitesForBillingProfile) ?? [],
    [transactionAccountId, data, cloudSitesForBillingProfileData]
  );
  const totalCount = useMemo(
    () =>
      (transactionAccountId
        ? data?.cloudSitesForTransactionAccount?.length
        : cloudSitesForBillingProfileData?.cloudSitesForBillingProfile?.length) ?? 0,
    [transactionAccountId, data, cloudSitesForBillingProfileData]
  );

  const errorDetails = error || cloudSitesForBillingProfileError || unprovisionedDataError;

  const columns = transactionAccountId ? columnsForTxa : columnsForBillingProfile;

  const renderActionsOnTop = () => {
    return [
      () =>
        shouldShowDownloadCSV ? (
          <div className="action-buttons">
            <DownloadSubscriptionToCsv
              cloudSiteIds={getCloudSiteIds(cloudSites)}
              isDisabled={!transactionAccountId && cloudSites?.length < 1}
              transactionAccountId={transactionAccountId as string}
              billingProfileId={billingProfileId as string}
            />
          </div>
        ) : (
          <></>
        ),
    ];
  };

  return (
    <div className="cloud-site-linked-subscriptions-tab">
      <CustomDynamicResultTable
        className="cloudsite-linked-subscriptions-table"
        testId="cloudsite-linked-subscriptions"
        heading={label}
        isLoading={loading || cloudSitesForBillingProfileLoading || unprovisionedDataLoading}
        error={errorDetails?.toString()}
        noResults={cloudSites?.length < 1 && isEmptyUnprovisionedData}
        emptyStateProps={emptyStateProps}
        totalCount={totalCount}
        boxShadow={true}
        actions={renderActionsOnTop()}
      >
        <ColGroup columns={columns} />
        <TableHead columns={columns} />
        <TableBody>
          {cloudSites.map((node: any, index: number) => {
            return (
              <>
                <tr
                  key={`cloud-site-linked-subscription-row-${node.cloudSiteId}`}
                  data-testid={`cloud-site-linked-subscription-row-${index}`}
                  className="white-row"
                >
                  <td colSpan={10} className="allow-overflow">
                    <CustomPanel
                      testId={`cloud-site-linked-subscription-${index}`}
                      expandedHeader={`Site: ${node.cloudSiteHostname}`}
                      collapsedHeader={`Site: ${node.cloudSiteHostname}`}
                    >
                      <ProductAppSubscriptionsContainer
                        cloudSiteId={node.cloudSiteId as string}
                        transactionAccountId={transactionAccountId as string}
                        billingProfileId={billingProfileId as string}
                      />
                    </CustomPanel>
                  </td>
                </tr>
              </>
            );
          })}

          {!isEmptyUnprovisionedData && (
            <tr data-testid={`cloud-site-linked-subscription-row-empty-cloudsite`} className="white-row">
              <td colSpan={10} className="allow-overflow">
                <CustomPanel
                  testId="cloud-site-linked-subscription-row-empty-cloudsite"
                  expandedHeader="Other"
                  collapsedHeader="Other"
                >
                  <ProductAppSubscriptionsContainer
                    transactionAccountId={transactionAccountId as string}
                    billingProfileId={billingProfileId as string}
                  />
                </CustomPanel>
              </td>
            </tr>
          )}
        </TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};

export const CloudSiteLinkedSubscriptionTabWithMemo = memo(CloudSiteLinkedSubscriptionTab);
